import React, { useContext, useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import MessageContext from '../../App/Contexts/MessageContext';
import ChatService from '../../services/ChatService';
import EventManager from '../../Util/EventManager';
import useNow from '../../Util/custom-hooks/useNow';
import parseServerDate from '../../Util/parseServerDate';
import ChatMessage from './ChatMessage';
import ChatComposer from './ChatComposer';
import { FILTER_ALL, filterHasRow, matchesFilter } from './ChatFilterMenu';

const EMPTY_SCOPE = { slideIds: [], question: false };

// A thread with replies opens expanded; `open` only records what the user has
// since toggled, so a thread that hasn't been touched still expands when new
// replies arrive.
const isExpanded = (open, chat) => open[chat.id] ?? true;

/**
 * Keys for the guest payload, which carries no ids of its own.
 *
 * Not the array index: the payload is newest-first, so every new message shifts
 * every index and a thread's expansion state would slide onto its neighbour.
 * Content is stable however the list moves. The counter only disambiguates an
 * exact duplicate - same author, timestamp and text - which React would
 * otherwise see as one row.
 */
function guestKeyer() {
    const seen = new Map();
    return (...parts) => {
        const base = parts.join('|');
        const count = seen.get(base) || 0;
        seen.set(base, count + 1);
        return count === 0 ? base : `${base}#${count}`;
    };
}

/**
 * Bring one thread into view inside the scroll container, top-aligned when the
 * whole thread fits and bottom-aligned when it doesn't - a new reply is the last
 * row inside its thread, so it is on screen either way. Returns false when the
 * row isn't rendered (filtered out, or the thread is gone).
 */
function scrollThreadIntoView(container, chatId) {
    // Matched by hand rather than by attribute selector: a guest row's key is
    // built from its own content and needs no escaping this way.
    const row = [...container.querySelectorAll('[data-chat-id]')].find(
        (el) => el.getAttribute('data-chat-id') === String(chatId)
    );
    if (!row) {
        return false;
    }
    const rowRect = row.getBoundingClientRect();
    const boxRect = container.getBoundingClientRect();
    container.scrollTop +=
        rowRect.height <= boxRect.height
            ? rowRect.top - boxRect.top
            : rowRect.bottom - boxRect.bottom;
    return true;
}

/**
 * Whether a loaded list carries a just-posted reply, by that reply's own id.
 *
 * Only by id: "the thread gained a reply" would also be true of someone else
 * replying to the same parent while this send is out, which would spend the
 * scroll on their reply and leave none for the one the user is waiting on.
 */
function carriesTheReply(chats, target) {
    if (!target.replyId) {
        return false;
    }
    const parent = chats.find((chat) => chat.id === target.chatId);
    return !!parent && parent.replies.some((reply) => reply.id === target.replyId);
}

/**
 * The chat panel container: loads the room's messages, keeps them scrolled to
 * the newest, and renders them as flat comment rows.
 *
 * Admins are highlighted by avatar colour. Member payloads carry a sender id, so
 * they match on `adminUserIds`; the guest payload only carries sender names, so
 * that path matches on `adminNames` instead.
 *
 * Owns both composer selections: `filter` (what the list shows) and `scope`
 * (what the next chat is tagged with). They live here rather than in the
 * composer because the filter counts the loaded messages, the scope has to be
 * cleared once a send lands, and the slide drawer sets the scope from outside.
 *
 * Threads live here too - which comment has its reply field open, that field's
 * draft, and which threads are collapsed. Only one field is open at a time, and
 * a draft has to outlive the reload that follows every chat event.
 */
export default function ({
    roomId = null,
    readOnly = false,
    messages = null,
    adminUserIds = [],
    adminNames = [],
    slides = [],
    // { slideId, question, key } from the slide drawer's compose buttons: tag
    // the next chat to that slide and put the caret in the composer. `key` is a
    // counter, so pressing the same slide's button twice refocuses.
    composeRequest = null,
    onComposeHandled // called once the request above has been applied
}) {
    const scrollRef = useRef();
    const [thisChat, setThisChat] = useState([]);
    // Distinguishes "this room has no messages" from "the request failed" -
    // without it the two render identically. See #166.
    const [loadFailed, setLoadFailed] = useState(false);
    const [filter, setFilter] = useState(FILTER_ALL);
    const [scope, setScope] = useState(EMPTY_SCOPE);
    // `{ chatId, anchorReplyId }` while a field is open, null otherwise.
    // `chatId` is the post target - the top-level comment - however deep the
    // user clicked; `anchorReplyId` is presentational only, naming the reply
    // the field should sit under, and is null when Reply was pressed on the
    // comment itself.
    const [replyTo, setReplyTo] = useState(null);
    const [replyDraft, setReplyDraft] = useState('');
    // Both a ref and state: the ref is what actually rejects a second submit,
    // because two Enters can land on the same render and the state one would
    // still read false in that closure. The state drives the disabled control.
    const replySendingRef = useRef(false);
    const [replySending, setReplySending] = useState(false);
    // Bumped on every Reply press so the field refocuses even when it was
    // already open on the same thread - see ChatMessage.
    const [replyFocusKey, setReplyFocusKey] = useState(0);
    // The same counter-to-refocus trick for the composer, driven from outside.
    const [composeFocusKey, setComposeFocusKey] = useState(0);
    const [openThreads, setOpenThreads] = useState({});
    const { setMessage } = useContext(MessageContext);
    // One interval for the whole list, so every time-ago label ticks together.
    const now = useNow();

    // `slides` is a fresh array on every render of the room page, so effects
    // key off this signature instead - and off the ids alone, since only a
    // slide appearing or disappearing changes anything below.
    const liveSlideIds = slides.map((slide) => slide.itemId);
    const liveSlideSignature = liveSlideIds.join(',');

    function initChat(roomId) {
        if (roomId == null) {
            return;
        }
        // A new load is a new scroll decision: the thread the last one settled
        // on stops governing here, rather than on first use - both of that
        // load's scrolls have to land on it, in whichever order they run.
        settledThreadRef.current = null;
        return ChatService.getChatMessagesByRoomId(roomId)
            .then((chatResponse) => {
                if (chatResponse) {
                    const chats = chatResponse.map((response) => {
                        const time = parseServerDate(response.chatMessageDate);
                        return {
                            id: response.chatMessageId,
                            name: `${response.sender.firstName} ${response.sender.lastName}`,
                            senderUserId: response.sender.userId,
                            message: response.text,
                            time: time,
                            slideTags: response.slideTags || [],
                            question: !!response.question,
                            // Already nested from the server, oldest-first
                            // within the thread; replies carry no tags.
                            replies: (response.replies || []).map((reply) => ({
                                id: reply.chatMessageId,
                                name: `${reply.sender.firstName} ${reply.sender.lastName}`,
                                senderUserId: reply.sender.userId,
                                message: reply.text,
                                time: parseServerDate(reply.chatMessageDate)
                            }))
                        };
                    });
                    const chatsReverse = chats.reverse();
                    setThisChat(chatsReverse);
                }
                setLoadFailed(false);
            })
            .catch((e) => {
                // Leaves whatever is already loaded on screen: stale messages
                // are more use than none, and the flag is what tells the
                // reader they are stale.
                setLoadFailed(true);
                console.log(e);
            })
            .then(() => {
                clearTimeout(trailingScrollRef.current);
                trailingScrollRef.current = setTimeout(scrollToBottom, 200);
            });
    }

    // A reply lands inside an existing thread and doesn't move that thread's
    // place in the list, so the scroll-to-newest that follows every refresh
    // would carry the viewport away from what was just posted. The thread is
    // recorded on submit and held - with no deadline - until a load carrying
    // that exact reply is seen, so a slow send or a slow event still lands on
    // it and every other load in the meantime scrolls normally.
    const replyScrollRef = useRef(null);
    // The loaded list as postReply sees it, which its own `thisChat` can't be:
    // that one is captured before the send and the list is reloaded during it.
    const thisChatRef = useRef([]);
    // Every load schedules a second, delayed scroll to catch rows that settle
    // late, so the thread a load settles on is kept for the rest of that load -
    // otherwise the delayed scroll undoes the first one. Cleared when the next
    // load begins.
    const trailingScrollRef = useRef(null);
    const settledThreadRef = useRef(null);

    const scrollToBottom = () => {
        const container = scrollRef.current;
        if (!container) {
            return;
        }
        const settled = settledThreadRef.current;
        if (settled !== null && scrollThreadIntoView(container, settled)) {
            return;
        }
        container.scrollTop = container.scrollHeight;
    };

    // Resolves true only when the chat actually landed; the composer keeps the
    // user's draft on anything else rather than destroying it.
    const postChatMessage = async (text) => {
        if (!text) {
            return false;
        }
        try {
            await ChatService.createChatMessage(
                roomId,
                text,
                new Date().toISOString(),
                scope.slideIds,
                scope.question
            );
        } catch (e) {
            // The server's own text is unfit to show: a refusal can surface raw
            // internals (a varchar overflow arrives as a Hibernate DataException)
            // or bare ids. Keep the detail in the console and tell the user the
            // one thing they can act on - the draft is still in the composer.
            console.log(e);
            setMessage(e.code === 'ECONNABORTED'
                ? 'Sending took too long. Check the chat before trying again.'
                : 'Your message was not sent. Please try again.');
            return false;
        }
        // Only clear the scope once the chat landed, so a failed send doesn't
        // silently drop the tags the user picked.
        setScope(EMPTY_SCOPE);
        return true;
    };

    const openReply = (chatId, anchorReplyId, prefill) => {
        // Switching fields mid-send would hand the in-flight reply's completion
        // a draft that belongs to another thread, and closeReply would then
        // clear it. Moving the field to another anchor within the same thread
        // is refused for the same reason: the draft is the one being sent.
        if (replySendingRef.current) {
            return;
        }
        setReplyTo({ chatId, anchorReplyId });
        setReplyDraft(prefill);
        setReplyFocusKey((key) => key + 1);
    };

    const closeReply = () => {
        setReplyTo(null);
        setReplyDraft('');
    };

    // Escape can't call closeReply directly: the send it appears to cancel is
    // already on its way and will land regardless, and clearing the field would
    // only lose whatever the user typed after submitting.
    const cancelReply = () => {
        if (!replySendingRef.current) {
            closeReply();
        }
    };

    // Replies carry no scope of their own - they inherit the parent's - so the
    // composer's slide tags never travel with one.
    const postReply = async (parentChatMessageId) => {
        const text = replyDraft.trim();
        // The field stays open and editable while a reply is in flight, so
        // without this a second Enter or click posts the same reply twice.
        if (!text || replySendingRef.current) {
            return;
        }
        replySendingRef.current = true;
        setReplySending(true);
        // Recorded before the send, not after: the chat event that triggers the
        // refetch can arrive before this request's own response does.
        replyScrollRef.current = { chatId: parentChatMessageId, replyId: null };
        try {
            const created = await ChatService.createChatMessage(
                roomId,
                text,
                new Date().toISOString(),
                [],
                false,
                parentChatMessageId
            );
            const target = replyScrollRef.current;
            if (target && target.chatId === parentChatMessageId) {
                target.replyId = created ? created.chatMessageId : null;
                if (!target.replyId) {
                    // Nothing left to recognise the reply by, so stop holding a
                    // target that can never match and scroll as usual from here.
                    replyScrollRef.current = null;
                } else if (carriesTheReply(thisChatRef.current, target)) {
                    // The load carrying it can arrive before this response does,
                    // and no further one is coming - so settle on it here rather
                    // than wait for a load that has already been and gone.
                    replyScrollRef.current = null;
                    settledThreadRef.current = target.chatId;
                    scrollToBottom();
                }
            }
        } catch (e) {
            // As with a chat: the server's own wording is unfit to show, and
            // the draft stays in the field so nothing typed is lost.
            console.log(e);
            setMessage(e.code === 'ECONNABORTED'
                ? 'Sending took too long. Check the chat before trying again.'
                : 'Your reply was not sent. Please try again.');
            replyScrollRef.current = null;
            return;
        } finally {
            replySendingRef.current = false;
            setReplySending(false);
        }
        // Expand the thread the reply lands in, so it isn't posted into a
        // collapsed one.
        setOpenThreads((previous) => ({ ...previous, [parentChatMessageId]: true }));
        closeReply();
    };

    const toggleThread = (chat) =>
        setOpenThreads((previous) => ({ ...previous, [chat.id]: !isExpanded(previous, chat) }));

    useEffect(() => {
        if (readOnly) return;
        initChat(roomId);
        EventManager.reactChatEvent(() => {
            initChat(roomId);
        });
        return () => {
            EventManager.unsetChatEvent();
        };
        // roomId is null on the room page's first render (thisRoom starts empty),
        // so a mount-only effect fetched nothing and never retried - the panel
        // then stayed empty until something happened to remount it.
    }, [readOnly, roomId]);

    // Slide tags are resolved server-side when messages load, so deleting a
    // slide leaves every loaded chip claiming the slide is still live. Deletion
    // arrives as PRESENTATION_CHANGE, which refreshes the room (and so `slides`)
    // but never fires a chat event - refetch here so the muted chip, its
    // tooltip and the filter row appear without waiting for a reload.
    //
    // Only on a slide *disappearing*: slides also arrive one at a time as their
    // images download, and refetching on each of those would be pure noise.
    // Deriving `deleted` from `slides` instead is not an option - it only holds
    // slides whose image has loaded, so a slow one would read as deleted.
    const knownSlideIdsRef = useRef(null);
    useEffect(() => {
        if (readOnly) return;
        const previous = knownSlideIdsRef.current;
        knownSlideIdsRef.current = liveSlideIds;
        if (previous === null) return;
        const live = new Set(liveSlideIds);
        if (previous.some((itemId) => !live.has(itemId))) {
            initChat(roomId);
        }
    }, [liveSlideSignature]);

    // A slide deleted while it was picked would otherwise sit in the scope and
    // fail every send until the user noticed and chose Room by hand.
    useEffect(() => {
        if (readOnly) return;
        setScope((previous) => {
            const live = new Set(liveSlideIds);
            const kept = previous.slideIds.filter((itemId) => live.has(itemId));
            return kept.length === previous.slideIds.length
                ? previous
                : { ...previous, slideIds: kept };
        });
    }, [liveSlideSignature]);

    // A compose button on a slide thumbnail means "talk about *this* slide", so
    // it replaces whatever scope was picked rather than adding to it. Keyed on
    // the counter alone: the same slide pressed twice has to focus the composer
    // again, and on mobile the panel switches tabs, which remounts this whole
    // panel with the request already in hand.
    //
    // The filter drops to All with it. Left alone it would go on hiding what
    // the button just invited: press chat on slide 4 under the Room filter and
    // the tagged message you send is excluded the moment it arrives. That is
    // the same silent hiding the deleted-slide effect below guards against,
    // reached from this entry point instead - and filterHasRow cannot catch it,
    // since Room always has a row to sit on.
    //
    // Handing the request back is what keeps that remount from re-applying it:
    // a tag chosen and sent an hour ago must not come back the next time the
    // reader taps the Chat tab.
    const composeKey = composeRequest ? composeRequest.key : null;
    useEffect(() => {
        if (readOnly || composeKey == null) return;
        setScope({
            slideIds: composeRequest.slideId ? [composeRequest.slideId] : [],
            question: !!composeRequest.question
        });
        setFilter(FILTER_ALL);
        setComposeFocusKey((key) => key + 1);
        if (onComposeHandled) {
            onComposeHandled();
        }
    }, [composeKey]);

    // The same hazard for the filter, which has no "fall back to Room" of its
    // own: a slide picked while it was live and untagged loses its row the
    // moment it is deleted, and the button then reads "All" while the list goes
    // on hiding every message, new ones included. Depends on the messages too,
    // because a chat still tagged to a deleted slide is what keeps its row.
    useEffect(() => {
        if (!filterHasRow(filter, thisChat, slides)) {
            setFilter(FILTER_ALL);
        }
    }, [filter, thisChat, liveSlideSignature]);

    // When readOnly, use messages passed via prop. The guest payload's dates go
    // through the same parser as the member path, so both surfaces show the
    // same age for a message no matter which API served it.
    useEffect(() => {
        if (readOnly && messages) {
            const keyOf = guestKeyer();
            const chats = messages.map((msg) => ({
                id: keyOf(msg.chatMessageDate, msg.senderFirstName, msg.senderLastName, msg.text),
                name: `${msg.senderFirstName} ${msg.senderLastName}`,
                senderUserId: null,
                message: msg.text,
                time: parseServerDate(msg.chatMessageDate),
                slideTags: msg.slideTags || [],
                question: !!msg.question,
                // Replies stay oldest-first inside the thread; only the
                // top-level order is reversed below.
                replies: (msg.replies || []).map((reply) => ({
                    id: keyOf(
                        reply.chatMessageDate,
                        reply.senderFirstName,
                        reply.senderLastName,
                        reply.text
                    ),
                    name: `${reply.senderFirstName} ${reply.senderLastName}`,
                    senderUserId: null,
                    message: reply.text,
                    time: parseServerDate(reply.chatMessageDate)
                }))
            }));
            // Both payloads arrive newest-first and both are reversed here, so
            // the panel reads oldest-at-top with the newest by the composer.
            setThisChat(chats.reverse());
        }
    }, [readOnly, messages]);

    // Scroll to bottom when new messages arrive - unless this is the load that
    // brought back a reply just posted, in which case follow that thread.
    useEffect(() => {
        thisChatRef.current = thisChat;
        const target = replyScrollRef.current;
        if (target && carriesTheReply(thisChat, target)) {
            replyScrollRef.current = null;
            settledThreadRef.current = target.chatId;
        }
        scrollToBottom();
    }, [thisChat]);

    const visibleChat = thisChat.filter((chat) => matchesFilter(chat, filter));

    const adminIdSet = new Set(adminUserIds);
    const adminNameSet = new Set(adminNames.map((name) => (name || '').trim().toLowerCase()));
    const isAdminSender = (chat) =>
        chat.senderUserId != null
            ? adminIdSet.has(chat.senderUserId)
            : adminNameSet.has((chat.name || '').trim().toLowerCase());

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                backgroundColor: 'common.white'
            }}
        >
            {/* Sits outside the scroll area on purpose: inside it, the
                scroll-to-newest that follows every load would carry it out of
                view exactly when it is needed. Shown whenever the load failed,
                with or without messages behind it - a failed refresh over a
                loaded list leaves the reader looking at stale messages, which
                is the same "no way to tell working from broken" this fixes. */}
            {loadFailed && (
                <Typography
                    role="alert"
                    sx={{
                        flex: '0 0 auto',
                        fontSize: 12,
                        color: 'error.main',
                        backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
                        textAlign: 'center',
                        py: 1,
                        px: 2
                    }}>
                    Couldn&apos;t load messages. Refresh to try again.
                </Typography>
            )}

            {/* Messages area - scrollable */}
            <Box
                ref={scrollRef}
                sx={{
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    pt: 1.5,
                    px: 2,
                    pb: 2
                }}
            >
                {visibleChat.length === 0 ? (
                    // Silent when the load failed: the banner already says so,
                    // and claiming the room is empty would contradict it.
                    !loadFailed && (
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: 'text.secondary',
                                textAlign: 'center',
                                py: 6,
                                px: 1
                            }}>
                            No chats with this tag yet.
                        </Typography>
                    )
                ) : (
                    <List
                        id={'chat-list'}
                        sx={{ borderColor: 'rgba(0,0,0,0)', padding: 0 }}
                    >
                        {visibleChat.map((chat) => (
                            <ListItem
                                key={chat.id}
                                data-chat-id={chat.id}
                                disablePadding
                                sx={{ display: 'block' }}>
                                <ChatMessage
                                    time={chat.time}
                                    message={chat.message}
                                    name={chat.name}
                                    now={now}
                                    isAdmin={isAdminSender(chat)}
                                    slideTags={chat.slideTags}
                                    question={chat.question}
                                    replies={chat.replies.map((reply) => ({
                                        ...reply,
                                        isAdmin: isAdminSender(reply)
                                    }))}
                                    readOnly={readOnly}
                                    expanded={isExpanded(openThreads, chat)}
                                    onToggleExpanded={() => toggleThread(chat)}
                                    replying={!!replyTo && replyTo.chatId === chat.id}
                                    replyAnchorId={replyTo ? replyTo.anchorReplyId : null}
                                    replyDraft={replyDraft}
                                    replyFocusKey={replyFocusKey}
                                    replySending={replySending}
                                    onReply={(anchorReplyId, prefill) =>
                                        openReply(chat.id, anchorReplyId, prefill)
                                    }
                                    onReplyDraftChange={setReplyDraft}
                                    onReplySubmit={() => postReply(chat.id)}
                                    onReplyCancel={cancelReply}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
            {/* Composer - fixed at the bottom (hidden for read-only guests) */}
            {!readOnly && (
                <ChatComposer
                    onSend={postChatMessage}
                    scope={scope}
                    onScopeChange={setScope}
                    filter={filter}
                    onFilterChange={setFilter}
                    chats={thisChat}
                    slides={slides}
                    focusKey={composeFocusKey}
                />
            )}
        </Box>
    );
}
